import { Moment } from 'moment'
import _ from 'lodash'
import type { ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import type { ContractVersionsPayrollPolicies } from 'modules/contracts/store/collections/contractVersionsPayrollPolicies'
import type { Employees } from 'modules/employees/store/collections/employees'
import type Employee from 'modules/employees/store/models/Employee'
import type Policy from 'modules/payroll_bundle/store/models/Policy'

import type ContractVersion from 'modules/contracts/store/models/ContractVersion'

const sortContractVersionsByEffectiveOn = (models: ContractVersion[]) => {
  return _.sortBy(models, (cv: ContractVersion) => cv.effectiveOn)
}

// Returns the subset of `models` (which should be the contract versions for
// an employee) that were effective (aka "current") between `startDate` and
// `endDate`.
const filterContractVersionsByCurrentWithinDateRange = (
  models: ContractVersion[],
  startDate: moment.Moment,
  endDate: moment.Moment
): ContractVersion[] => {
  const sortedModels = sortContractVersionsByEffectiveOn(models)

  return sortedModels.filter(
    (model, index) =>
      model.effectiveOn.isSameOrBefore(endDate, 'day') &&
      (index === sortedModels.length - 1 || // last one?
        sortedModels[index + 1]?.effectiveOn.isAfter(startDate, 'day'))
  )
}

const currentContractVersionsWithinDateRange = (
  contractVersions: ContractVersions,
  startDate: moment.Moment,
  endDate: moment.Moment
) => {
  return _.chain(contractVersions.models)
    .groupBy((c) => c.attributes.employee_id)
    .map((groupedByEmployee) =>
      filterContractVersionsByCurrentWithinDateRange(groupedByEmployee, startDate, endDate)
    )
    .flatten()
    .value()
}

/**
 * It returns a list of active employees for a specific policy.
 *
 * We consider an employee active when:
 *
 * - The employee has an active contract when the date range defined by the contract's start and end date must overlap with the specified period.
 *   As a contract can contain multiple versions, we need first to get versions that are effective on the specified period.
 * - The employee does not have a termination date before the specified period.
 */
export default function filterActiveEmployeesByPolicy({
  employees,
  policy,
  contractVersions,
  contractVersionsPayrollPolicies,
  period,
}: {
  employees: Employees | Employee[]
  policy: Policy
  contractVersions: ContractVersions
  contractVersionsPayrollPolicies: ContractVersionsPayrollPolicies
  period: { startsOn: Moment; endsOn: Moment }
}): Employee[] {
  const activeContractVersions = currentContractVersionsWithinDateRange(
    contractVersions,
    period.startsOn,
    period.endsOn
  )
  const activePolicyContracts = activeContractVersions.filter(
    (contract) =>
      contract.activeWithinDateRange(period.startsOn, period.endsOn) &&
      contractVersionsPayrollPolicies.find({ contract_version_id: contract.id })?.attributes
        .payroll_policy_id === policy.id
  )

  const policyEmployeeIdsWithActiveContract = new Set(
    activePolicyContracts.map((contract) => contract.attributes.employee_id)
  )

  const activeEmployees = employees.filter(
    (employee) =>
      policyEmployeeIdsWithActiveContract.has(employee.id) &&
      employee.activeWithinDateRange(period.startsOn, period.endsOn)
  )

  return activeEmployees
}
